import FreeGenerator from "../components/FreeGenerator";
import TrilliumIcon from "../components/TrilliumIcon";
import PublicPageHeader from "../components/PublicPageHeader";
import useDocumentMeta from "../lib/useDocumentMeta";

/**
 * /free-lesson-generator — dedicated, indexable page for the no-login
 * lesson generator (Part A of the SEO URLs lane, Jul 2026).
 *
 * Reuses the same `<FreeLessonForm />` component the homepage's
 * `#try-it` section renders — do NOT fork or rewrite its logic. This
 * page is additive: the homepage anchor stays exactly as it is; this
 * gives search engines a clean URL to rank against.
 *
 * Not linked from primary nav or footer. Reached by URL / search.
 *
 * Locked copy (title, meta, and the H1 + intro paragraph below)
 * is founder-approved word-for-word — the pytest tripwire
 * `test_free_lesson_generator_page.py` enforces this.
 */
export default function FreeLessonGeneratorPage() {
  useDocumentMeta({
    title: "Free Ontario Lesson Plan Generator — Grades 4–8 | Atomic Edu",
    description:
      "Generate a complete, curriculum-aligned Ontario lesson package for Grades 4–8. Three-part lesson, teacher plan, student handouts, assessment, and report card language. One free lesson a week, emailed to you.",
    ogTitle: "A complete Ontario lesson package. Free, once a week.",
    ogDescription:
      "The same full generator paid subscribers use — nothing trimmed. Choose your grade, subject, and curriculum expectation, and we'll email you the PDF.",
    image: "https://atomicedu.ca/og/free-lesson-generator.png",
    url: "https://atomicedu.ca/free-lesson-generator",
    type: "website",
  });

  return (
    <div
      className="min-h-screen bg-[#eef8f4] font-montserrat"
      data-testid="free-lesson-generator-page"
    >
      {/* Shared slate-navy public header — brand logo top-left, links
          to /. Same header renders on /lesson-from-resource. */}
      <PublicPageHeader />

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-10 sm:py-14 space-y-8">
        {/* Hero — LOCKED COPY A. Word-for-word: any tightening / rephrasing
            fails the pytest tripwire. */}
        <section className="text-center sm:text-left" data-testid="flg-hero">
          <span className="inline-flex items-center gap-2 rounded-full border border-[#4a6984]/15 bg-white px-3 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-[#4a6984]/80">
            <TrilliumIcon size={12} className="text-[#4a6984]" />
            For Ontario Grade 4–8 Teachers
          </span>
          <h1
            data-testid="flg-headline"
            className="mt-5 font-black text-4xl sm:text-5xl text-[#1c2d3d] tracking-tighter leading-[1.05]"
          >
            A complete Ontario lesson package. Free, once a week.
          </h1>
          <p
            data-testid="flg-intro"
            className="mt-5 text-[#4a6984] font-medium text-base sm:text-lg leading-relaxed max-w-2xl mx-auto sm:mx-0"
          >
            Choose your grade, subject, and curriculum expectation. You&apos;ll get
            the same full lesson package paid subscribers get &mdash; three-part
            lesson, teacher plan, student handouts, assessment, and report card
            language. Nothing trimmed.
          </p>
          <p
            data-testid="flg-intro-2"
            className="mt-3 text-[#4a6984] font-medium text-base sm:text-lg leading-relaxed max-w-2xl mx-auto sm:mx-0"
          >
            We&apos;ll email you the PDF. No account, no credit card &mdash; just an
            email address, one free lesson every seven days.
          </p>
        </section>

        {/* The generator itself — REUSED, not forked. Same component
            the homepage `#try-it` section renders. */}
        <FreeGenerator />

        <p
          data-testid="flg-social-proof"
          className="text-center text-[11px] font-bold uppercase tracking-[0.18em] text-[#4a6984]/55"
        >
          Ontario curriculum-aligned <span className="mx-2">·</span>
          Grades 4–8 <span className="mx-2">·</span>
          Skills-first pedagogy <span className="mx-2">·</span>
          7-day free trial
        </p>
      </main>
    </div>
  );
}
